<template>
  <form>
    <div class="modal-card" style="width:960px">
      <header class="modal-card-head">
        <p class="modal-card-title">Inspect Return</p>
      </header>
      <section class="modal-card-body">
        <div v-if="!loadingReturns && selectedRma !== null">
          <!-- RMA Details -->
          <p class="has-text-weight-bold mb-sm">RMA Details</p>
          <table class="table is-fullwidth is-striped is-narrow is-size-7 border">
            <tbody>
              <tr>
                <th>ID</th>
                <td>{{ selectedRma.id }}</td>
              </tr>
              <tr>
                <th>RMA Number</th>
                <td>
                  <p v-if="selectedRma.rma_number">{{ selectedRma.rma_number }}</p>
                  <p v-else class="has-text-grey-light is-italic">None Provided</p>
                </td>
              </tr>
              <tr>
                <th>PO #</th>
                <td>
                  <router-link class="has-text-weight-bold has-text-info"
                    :to="{ name: 'order.summary', params: { id: selectedRma.order_id } }">{{
                      selectedRma.purchase_order_number }}</router-link>
                </td>
              </tr>
              <tr>
                <th>Customer Order #</th>
                <td>
                  <p v-if="selectedRma.customer_order_number">{{ selectedRma.customer_order_number }}</p>
                  <p v-else class="has-text-grey-light is-italic">None Provided</p>
                </td>
              </tr>
              <tr>
                <th>Total RMA Value</th>
                <td class="has-text-danger has-text-weight-bold">
                  <CurrencyLocale :value="parseFloat(selectedRma.locale_total_amount)"
                    :currency="selectedRma.locale_currency" />
                </td>
              </tr>
              <tr>
                <th>Reason</th>
                <td>{{ selectedRma.reason }}</td>
              </tr>
              <tr>
                <th>Status</th>
                <td>
                  <Status :rma="selectedRma" />
                </td>
              </tr>
            </tbody>
          </table>
          <!-- Return Details -->
          <p class="has-text-weight-bold mb-sm">Return Details</p>
          <table class="table is-fullwidth is-striped is-narrow is-size-7 border">
            <tbody>
              <tr>
                <th>Status</th>
                <td>
                  <span v-if="returnDetails?.status" class="has-text-weight-bold"
                    :class="returnDetails?.status === 'approved' ? 'has-text-success' : 'has-text-warning'">{{
                      $utils.upperFirst(returnDetails?.status) }}</span>
                  <span v-else> N/A </span>
                </td>
              </tr>
              <tr>
                <th>Return Tracking Number</th>
                <td>{{ trackingNumbers }}</td>
              </tr>
              <tr v-if="carrier !== null">
                <th>Return Carrier</th>
                <td>{{ carrier.name }}</td>
              </tr>
              <tr v-if="context.isMerchant() && returnDetails?.inspection_notes">
                <th>Inspection Notes</th>
                <td>{{ returnDetails.inspection_notes }}</td>
              </tr>
            </tbody>
          </table>
          <!-- RMA Lines -->
          <div class="mb-lg">
            <div class="columns is-align-items-center">
              <div class="column has-text-weight-bold">
                Return Line Items
              </div>
            </div>
            <table class="table is-fullwidth is-vcentered is-striped is-size-7 is-narrow border">
              <thead>
                <tr>
                  <th>Item</th>
                  <th class="has-text-centered" style="width:96px">Qty Returned</th>
                  <th v-if="showReturnFields" class="has-text-centered" style="width:112px">Qty Approved</th>
                  <th v-if="showReturnFields" class="has-text-centered" style="width:112px">Qty Rejected</th>
                  <th v-if="showReturnFields">{{ isInspectionRequired ? 'Condition' : 'Condition (Optional)' }}</th>
                  <th v-if="showReturnFields">{{ isInspectionRequired ? 'Inspection Note' : 'Inspection Note (Optional)' }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="line in enrichedRmaLines" :key="line.id">
                  <td>
                    <Identifiers :variant="line.variant" nameInteraction="none" :showImage="false" />
                  </td>
                  <td class="has-text-centered">{{ line.quantity_returned }}</td>
                  <td v-if="showReturnFields" class="has-text-centered">
                    <b-field>
                      <b-numberinput
                        v-model="line.quantity_approved"
                        :controls="false"
                        :min="0"
                        :max="line.quantity_returned"
                        :disabled="disableFields"
                        expanded />
                    </b-field>
                  </td>
                  <td v-if="showReturnFields" class="has-text-centered">
                    <b-field>
                      <b-numberinput
                        v-model="line.quantity_rejected"
                        :controls="false"
                        :min="0"
                        :max="line.quantity_returned"
                        :disabled="disableFields"
                        expanded />
                    </b-field>
                  </td>
                  <td v-if="showReturnFields">
                    <b-field>
                      <b-select :disabled="disableFields" v-model="line.condition" placeholder="Select Condition">
                        <option value="new">New</option>
                        <option value="used">Used</option>
                        <option value="damaged">Damaged</option>
                        <option value="missing">Missing</option>
                        <option value="mixed">Mixed</option>
                      </b-select>
                    </b-field>
                  </td>
                  <td v-if="showReturnFields">
                    <b-tooltip
                      v-if="disableFields"
                      :label="line.inspection_notes || 'No note provided'"
                      position="is-left"
                      size="is-small"
                      multilined>
                      <span style="display:block">
                        <b-field>
                          <b-input
                            v-model="line.inspection_notes"
                            type="text"
                            placeholder="Note..."
                            disabled />
                        </b-field>
                      </span>
                    </b-tooltip>
                    <b-field v-else message="">
                      <b-input
                        v-model="line.inspection_notes"
                        type="text"
                        placeholder="Note..."
                        :required="isInspectionRequired" />
                    </b-field>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <!-- Global Inspection Note -->
          <div v-if="showReturnFields" class="mb-lg">
            <b-field :label="isInspectionRequired ? 'Inspection Note' : 'Inspection Note (Optional)'" expanded>
              <b-input autocomplete="off" type="textarea" v-model="inspectionNote"
                :required="isInspectionRequired" :disabled="disableFields"></b-input>
            </b-field>
          </div>
          <!-- RMA Messages -->
          <div v-if="selectedRma.memos.length" class="mb-lg">
            <p class="has-text-weight-bold mb-sm">Return Messages</p>
            <div v-for="memo in selectedRma.memos" :key="memo.id">
              <Memo :memo="memo" />
            </div>
          </div>
        </div>
        <!-- Loading -->
        <div v-else>
          <loading message="Loading item..." />
        </div>
      </section>
      <footer class="modal-card-foot space-between">
        <b-button @click="$emit('close')">Close</b-button>
        <div class="buttons" v-if="(context.isSupplier() || isReset) && !loadingReturns && !returnHandled">
          <b-tooltip :label="rejectTooltip" :active="!isValidReject" position="is-top" multilined class="mr-2">
            <b-button type="is-danger" @click="patchReturn('rejected')" :loading="rejectingReturn"
              :disabled="!isValidReject">Reject Return</b-button>
          </b-tooltip>
          <b-tooltip :label="approveTooltip" :active="!isValidApprove" position="is-top" multilined size="is-small">
            <b-button type="is-success" @click="patchReturn('approved')" :loading="approvingReturn"
              :disabled="!isValidApprove">Approve Return</b-button>
          </b-tooltip>
        </div>
        <div class="buttons" v-if="context.isMerchant() && !loadingReturns && (returnDetails?.status === 'approved' || returnDetails?.status === 'rejected')">
          <b-button type="is-warning" @click="resetReturn" :loading="resettingReturn">Modify Return</b-button>
        </div>
      </footer>
    </div>
  </form>
</template>

<script>
import { mapGetters } from 'vuex'
import Identifiers from '@/components/Models/Variant/Identifiers'
import Status from '@/components/Presenters/Rma/Status'
import Memo from '@/components/Models/Memo/Memo'

export default {
  components: {
    Identifiers,
    Status,
    Memo
  },
  data () {
    return {
      enrichedRmaLines: [],
      inspectionNote: '',
      isInspectionRequired: false,
      approvingReturn: false,
      rejectingReturn: false,
      resettingReturn: false
    }
  },
  computed: {
    ...mapGetters(['context']),
    endpoint () {
      return this.context.route + '/rmas/' + this.selectedRma.id
    },
    returnHandled () {
      return this.returnDetails && this.returnDetails.status !== 'pending' && !this.isReset
    },
    isReset () {
      if (!this.returnDetails) return false
      return (this.returnDetails.approved_at !== null || this.returnDetails.rejected_at !== null) &&
        this.returnDetails.status !== 'approved' &&
        this.returnDetails.status !== 'rejected'
    },
    showInspection () {
      return this.context.isSupplier() && this.carrier !== null
    },
    showOnMerchant () {
      return this.context.isMerchant() && (!!this.returnHandled || this.isReset)
    },
    showReturnFields () {
      return this.showInspection || this.showOnMerchant
    },
    disableFields () {
      return (this.returnHandled || this.showOnMerchant) && !this.isReset
    },
    inspectionErrors () {
      const errors = []
      if (this.isInspectionRequired) {
        if (!this.inspectionNote || this.inspectionNote.trim() === '') {
          errors.push('Inspection note required')
        }
        const linesWithoutCondition = this.enrichedRmaLines.filter(line => !line.condition || line.condition.trim() === '')
        if (linesWithoutCondition.length > 0) {
          errors.push(`${linesWithoutCondition.length} item(s): condition required`)
        }
        const linesWithoutNote = this.enrichedRmaLines.filter(line => !line.inspection_notes || line.inspection_notes.trim() === '')
        if (linesWithoutNote.length > 0) {
          errors.push(`${linesWithoutNote.length} item(s): inspection note required`)
        }
      }
      return errors
    },
    canEditLines () {
      return this.showInspection || (this.context.isMerchant() && this.isReset)
    },
    isValidApprove () {
      if (!this.carrier) return false
      if (this.canEditLines) {
        const valid = this.enrichedRmaLines.every(line => {
          const total = line.quantity_approved + line.quantity_rejected
          return total > 0 && total <= line.quantity_returned
        })
        if (!valid) return false
        if (!this.enrichedRmaLines.some(line => line.quantity_approved > 0)) return false
      }
      return this.inspectionErrors.length === 0
    },
    isValidReject () {
      if (!this.carrier) return false
      if (this.canEditLines) {
        const valid = this.enrichedRmaLines.every(line =>
          line.quantity_approved === 0 && line.quantity_rejected > 0 && line.quantity_rejected <= line.quantity_returned
        )
        if (!valid) return false
      }
      return this.inspectionErrors.length === 0
    },
    approveTooltip () {
      if (!this.carrier) return 'No return label generated for this RMA.'
      const errors = []
      if (this.canEditLines) {
        const invalid = this.enrichedRmaLines.filter(line => {
          const total = line.quantity_approved + line.quantity_rejected
          return total <= 0 || total > line.quantity_returned
        })
        if (invalid.length > 0) errors.push(`${invalid.length} item(s): approved + rejected qty invalid`)
        if (!this.enrichedRmaLines.some(line => line.quantity_approved > 0)) {
          errors.push('Min 1 qty approved required')
        }
      }
      return [...errors, ...this.inspectionErrors].join(' , ')
    },
    rejectTooltip () {
      if (!this.carrier) return 'No return label generated for this RMA.'
      const errors = []
      if (this.canEditLines) {
        const invalid = this.enrichedRmaLines.filter(line =>
          line.quantity_approved !== 0 || line.quantity_rejected <= 0 || line.quantity_rejected > line.quantity_returned
        )
        if (invalid.length > 0) errors.push(`${invalid.length} item(s): approved must be 0, rejected qty invalid`)
      }
      return [...errors, ...this.inspectionErrors].join(' , ')
    }
  },
  methods: {
    loadConnections () {
      const endpoint = this.context.route + '/connections/'
      const query = {
        params: { pagination: 0, short: 1, retailer_id: this.selectedRma.retailer.id }
      }
      this.$http.get(endpoint, query).then(response => {
        const connectionDetails = response.data.results
        if (connectionDetails.length) {
          const { options } = connectionDetails[0]
          this.isInspectionRequired = !!this.$utils.find(options, ['code', 'inspection_required'])
        }
      })
    },
    initLines () {
      this.inspectionNote = this.returnDetails?.inspection_notes || ''
      const existingLines = this.returnDetails?.rma_return_lines || []
      this.enrichedRmaLines = this.selectedRma.rma_lines.map(line => {
        const match = existingLines.find(existing => existing.variant.id === line.variant.id)
        const quantityReturned = match?.quantity_returned || line.quantity
        return {
          ...line,
          quantity_returned: quantityReturned,
          quantity_approved: match?.quantity_approved || 0,
          quantity_rejected: match?.quantity_rejected || 0,
          condition: match?.condition || '',
          inspection_notes: match?.inspection_notes || ''
        }
      })
    },
    patchReturn (action) {
      const statusMap = { approved: 'approvingReturn', rejected: 'rejectingReturn' }
      this[statusMap[action]] = true
      const json = {
        status: action,
        inspection_notes: this.inspectionNote,
        rma_return_lines: this.enrichedRmaLines.map(line => ({
          variant: { id: line.variant.id },
          quantity_approved: line.quantity_approved,
          quantity_rejected: line.quantity_rejected,
          condition: line.condition,
          inspection_notes: line.inspection_notes
        }))
      }
      this.$http.patch(`${this.endpoint}/returns/${this.returnDetails?.id}/`, json).then(() => {
        this.$buefy.toast.open({ type: 'is-success', message: 'Return updated successfully.' })
      }).catch(() => {
        this.$buefy.toast.open({ type: 'is-danger', message: `Your attempt to ${action} this return failed. Please refresh and try again.` })
      }).finally(() => {
        this[statusMap[action]] = false
        this.$emit('close')
      })
    },
    resetReturn () {
      this.resettingReturn = true
      this.$http.put(`${this.endpoint}/returns/${this.returnDetails?.id}/reset/`).then(() => {
        this.$buefy.toast.open({ type: 'is-success', message: 'Return reset successfully.' })
      }).catch(() => {
        this.$buefy.toast.open({ type: 'is-danger', message: 'Failed to reset the return. Please refresh and try again.' })
      }).finally(() => {
        this.resettingReturn = false
        this.$emit('close')
      })
    }
  },
  watch: {
    returnDetails (val) {
      if (val) {
        this.initLines()
      }
    }
  },
  mounted () {
    if (this.returnDetails) {
      this.initLines()
    }
    if (this.context.isSupplier()) {
      this.loadConnections()
    }
  },
  props: {
    selectedRma: { required: true },
    returnDetails: { default: null },
    carrier: { default: null },
    trackingNumbers: { default: 'N/A' },
    loadingReturns: { type: Boolean, default: false }
  }
}
</script>
